import Core from '../plugin-core-system/core.class.js';
import Tobii from '@midzer/tobii';
import TranslatedLang from '../helper/translated-lang.helper.js';

export default class CsLightbox extends Core {

  static options = {
    zoomImageSelector: '[data-type="image"] img, .js-zoom-image'
  };

  applyTobiiWheelFix() {
    const minScale = 1;
    const maxScale = 4;
    const tolerance = 0.01;

    const getScale = (image) => {
      const transform = getComputedStyle(image).transform;

      if (!transform || transform === 'none') {
        return minScale;
      }

      try {
        const matrix = new DOMMatrixReadOnly(transform);

        return Math.hypot(matrix.a, matrix.b);
      } catch {
        return minScale;
      }
    };

    this.lightbox.addEventListener(
      'wheel',
      (event) => {
        const target = event.target instanceof Element ? event.target : null;

        const image = target?.closest('img');

        const isZoomImage = image?.matches(
          CsLightbox.options.zoomImageSelector
        );

        // Keep native scrolling for non-zoomable content.
        if (!isZoomImage || event.deltaY === 0) {
          event.stopImmediatePropagation();
          return;
        }

        const scale = getScale(image);
        const zoomsIn = event.deltaY < 0;
        const zoomsOut = event.deltaY > 0;

        const isAtMinimum = scale <= minScale + tolerance;
        const isAtMaximum = scale >= maxScale - tolerance;

        // Keep native scrolling when the zoom limit is reached.
        if ((zoomsOut && isAtMinimum) || (zoomsIn && isAtMaximum)) {
          event.stopImmediatePropagation();
        }
      },
      {
        capture: true,
        passive: true
      }
    );
  }

  setCloseButtonPosition(closeButton, innerPosition = false, activeSlideMedia = null, delay = 0) {
    if (delay > 0) {
      closeButton.classList.remove('active');
    }

    if (!innerPosition) {
      closeButton.removeAttribute('style');
    } else {
      const containerRect = this.lightbox.getBoundingClientRect();
      const mediaRect = activeSlideMedia.getBoundingClientRect();

      const buttonHalfWidth = closeButton.offsetWidth / 2;
      const buttonHalfHeight = closeButton.offsetHeight / 2;

      closeButton.style.top = `${mediaRect.top - buttonHalfHeight}px`;

      const spaceOnSide = (containerRect.width - mediaRect.width) / 2;
      closeButton.style.right = spaceOnSide < closeButton.offsetWidth ? '0' : `${spaceOnSide - buttonHalfWidth}px`;
    }

    if (delay > 0) {
      setTimeout (() => {
        closeButton.classList.add('active');
      }, delay);
    } else {
      closeButton.classList.add('active');
    }
  }

  checkCloseButtonPosition(delay = 0) {
    const closeButton = this.lightbox.querySelector('.tobii__btn--close');
    if (!(closeButton instanceof HTMLElement) || !this.tobii.isOpen()) {
      // consider resize event could be triggered if lightbox is closed
      return;
    }

    // check if close button position is set to inner
    const currentGroup = this.tobii.currentGroup();
    const trigger = document.querySelector(`.js-lightbox[data-group="${currentGroup}"]`);
    const activeSlideContainer = this.lightbox.querySelector('.tobii__slider[aria-hidden="false"]');
    if (!(activeSlideContainer instanceof HTMLElement) || !trigger?.closest('.js-lightbox-close-button-inner')) {
      this.setCloseButtonPosition(closeButton);
      return;
    }

    const slides = activeSlideContainer.querySelectorAll('.tobii__slide');
    const activeSlide = slides[this.tobii.slidesIndex()];
    const activeSlideMedia = activeSlide?.querySelector('.tobii-image, .tobii-html, [data-type="image"], [data-type="html"]');
    if (!(activeSlideMedia instanceof HTMLElement)) {
      this.setCloseButtonPosition(closeButton);
      return;
    }

    this.setCloseButtonPosition(closeButton, true, activeSlideMedia, delay);
  }

  handleCloseButtonPosition() {
    const delay = 250;

    this.tobii.on('open', () => this.checkCloseButtonPosition());
    this.tobii.on('previous', () => this.checkCloseButtonPosition(delay));
    this.tobii.on('next', () => this.checkCloseButtonPosition(delay));

    window.addEventListener('resize', () => this.checkCloseButtonPosition(delay));
  }

  initLightbox() {

    let langConfig = {};

    try {
      langConfig = TranslatedLang.translatedLangConfig('lightbox').csConfig;
    } catch (error){
      console.error('Labels for lightbox not defined', error);
    }

    // see https://github.com/midzer/tobii
    this.tobii = new Tobii({
      selector: '.js-lightbox',
      captionsSelector: 'self',
      captionAttribute: 'data-lightbox-caption',
      captionToggle: true,
      captionToggleLabel: [langConfig['captionToggleLabel.show'], langConfig['captionToggleLabel.hide']],
      navLabel: [langConfig['navLabel.prev'], langConfig['navLabel.next']],
      announcementLabel: [langConfig['announcementLabel.page'], langConfig['announcementLabel.of']],
      closeLabel: langConfig['closeLabel'],
      dialogTitle: langConfig['dialogTitle'],
      loadingIndicatorLabel: langConfig['loadingIndicatorLabel']
    });

    this.lightbox = document.querySelector('.tobii:not(.js-wheel-fix-initialized)');
    if (!this.lightbox) {
      return;
    }

    // fix for mouse wheel in tobii lightbox 3.2.0
    this.lightbox.classList.add('js-wheel-fix-initialized');
    this.applyTobiiWheelFix();

    // set custom close button position, if option is set
    this.handleCloseButtonPosition();
  }

  init() {
    this.initLightbox();
  }
}
